<script>
import { defineAsyncComponent } from "@common/defineAsyncComponent";
import { library } from '@common/library/library';
import { componentStore } from "@common/componentStore/componentStore";
import backdrop from "@common/backdrop/backdrop";

const editorSettings = {
    templateSettingsPanel: {
        type: 'panel',
        label: 'Template Settings',
        id: 'templateSettingsPanel',
        fields: [
            'headingTemplate',
            'mergeFields',
            'customMessageTemplate',
            'messageFields'
        ]
    },
    imageSettingsPanel: {
        type: 'panel',
        label: 'Image Settings',
        id: 'imageSettingsPanel',
        fields: [
            'backdropImageNotice',
            'messageImage',
            'messageImageWidth',
            'messageImageAlignment',
        ]
    },
    displaySettingsPanel: {
        type: 'panel',
        label: 'Display Settings',
        id: 'displaySettingsPanel',
        fields: [
            'allowDismiss',
            'dismissStyle',
            'showAsToast',
            'toastInitialText',
            'toastWidth'
        ]
    },
    backdropSettingsPanel: {
        type: 'panel',
        label: 'Backdrop Settings',
        id: 'backdropSettingsPanel',
        fields: [
            'backdropType',
            'backdropMinHeight',
            'backgroundImage'
        ]
    },
    colorSettingsPanel: {
        type: 'panel',
        label: 'Color Settings',
        id: 'colorSettingsPanel',
        fields: [
            'backgroundColor',
            'borderColor',
            'textColor'
        ]
    },
    marginSettingsPanel: {
        type: 'panel',
        label: 'Margin & Padding Settings',
        id: 'marginSettingsPanel',
        fields: [
            'messagePanelMargin',
            'messagePanelPadding',
            'messageItemPadding',
            'customMessagePanelMargin',
            'customMessagePanelPadding'
        ]
    },
    headingTemplate: {
        type: 'text',
        label: 'Heading Template',
        id: 'titleTemplate',
        default: '<h1>A heading</h1>',
        helpText: 'HTML for the title template.'
    },
    backdropType: {
        type: 'dropdown',
        label: 'Backdrop Type',
        id: 'backdropType',
        items: [
            'sunrise',
            'white void'
        ],
        helpText: 'Choose an animation, image, color or the invigorating "white void".'
    },
    backgroundImage: {
        type: 'upload',
        label: 'Backdrop Image',
        id: 'backgroundImage',
        allowedExtensions: '.png, .jpg, .jpeg',
        showAsDefaultInput: true,
        multiple: false,
        psp: 'component',
        helpText: 'Add an image to display as the background of the message panel.'
    },
    backdropImageNotice: {
        type: 'alert',
        text: '<strong>Note:</strong> If you want to set the background image, go to backdrop settings.'
    },
    messageImage: {
        type: 'upload',
        label: 'Message Image',
        id: 'messageImage',
        allowedExtensions: '.png, .jpg, .jpeg',
        showAsDefaultInput: true,
        multiple: false,
        psp: 'component',
        helpText: 'Add an image to display above the message template.'
    },
    messageImageWidth: {
        type: 'string',
        label: 'Message Image Width',
        id: 'messageImageWidth',
        default: '100%',
        message: 'Include a unit such as %, px, em  eg: 150px',
        helpText: 'Specify the width of the image.  The height of the image will be determined by the image\'s original aspect ratio.'
    },
    messageImageAlignment: {
        type: 'dropdown',
        label: 'Message Image Alignment',
        id: 'messageImageAlignment',
        items: ['left', 'right', 'center'],
        default: 'left',
        helpText: 'Specify how the image should be aligned if it does not use the full width of the available space.'
    },
    backdropMinHeight: {
        type: 'string',
        label: 'Backdrop Minimum Height',
        id: 'backdropMinHeight',
        message: 'eg: 300px or 40% or 50vh',
        helpText: 'Percentage height is based on the height of the parent element. vh height is a percentage based on the total window height.'
    },
    messagePanelMargin: {
        type: 'dropdown',
        label: 'Margin',
        id: 'messagePanelMargin',
        helpText: 'Add a margin around the message panel.',
        items: [
            '0',
            '5px',
            '10px',
            '15px',
            '20px',
            '25px',
            '30px',
            'custom'
        ],
        default: '10px'
    },
    messagePanelPadding: {
        type: 'dropdown',
        label: 'Padding',
        id: 'messagePanelPadding',
        helpText: 'Add a padding around the message panel.',
        items: [
            '0',
            '5px',
            '10px',
            '15px',
            '20px',
            '25px',
            '30px',
            'custom'
        ],
        default: '10px'
    },
    messageItemPadding: {
        type: 'dropdown',
        label: 'Padding',
        id: 'messageItemPadding',
        helpText: 'Add a padding around the each message.',
        items: [
            '0',
            '5px',
            '10px',
            '15px',
            '20px',
            '25px',
            '30px',
            'custom'
        ],
        default: '10px'
    },
    customMessagePanelMargin: {
        type: 'text',
        label: 'Custom Margin',
        id: 'customMessagePanelMargin',
        helpText: 'Custom margin is added by comma separated values for: top, right, bottom, left',
        message: 'eg: 5px, 10px, 10px, 50px',
        default: ''
    },
    customMessagePanelPadding: {
        type: 'text',
        label: 'Custom Padding',
        id: 'customMessagePanelPadding',
        helpText: 'Custom padding is added by comma separated values for: top, right, bottom, left',
        message: 'eg: 5px, 10px, 10px, 50px',
        default: ''
    },
    backgroundColor: {
        type: 'color',
        label: 'Background Color',
        id: 'backgroundColor',
        default: '#ffffffff'
    },
    borderColor: {
        type: 'color',
        label: 'Border Color',
        id: 'borderColor',
        default: '#cacacaff'
    },
    textColor: {
        type: 'color',
        label: 'Text Color',
        id: 'textColor',
        default: '#000000ff'
    },
    customMessageTemplate: {
        type: 'text',
        label: 'Custom Message Template',
        id: 'customMessageTemplate',
        default: '<p>A message.</p>',
        helpText: 'Provide a custom message using HTML and merged fields inside double curly braces eg: {{vol.VOL_FIRST_NAME}} or vol[\'UDF Group/A UDF Custom Code\'].'
    },
    allowDismiss: {
        type: 'boolean',
        label: 'Dismissable',
        helpText: 'Allow the message to be dismissable. This is only available when the field is part of a user profile.',
        default: false
    },
    dismissStyle: {
        type: 'dropdown',
        label: 'Dismiss Button Style',
        id: 'dismissStyle',
        items: [
            'text',
            'closeicon',
        ],
        default: 'text'
    },
    showAsToast: {
        type: 'boolean',
        label: 'Show the message initially as a toast.',
        id: 'showAsTost',
        default: false
    },
    toastInitialText: {
        type: 'text',
        label: 'Toast Short Text',
        id: 'toastInitialText',
        default: false
    },
    toastWidth: {
        type: 'string',
        label: 'Toast Width',
        id: 'toastWidth'
    },
    mergeFields: {
        label: 'Message Merge Fields',
        id: 'mergeFields',
        type: 'dropdowntree',
        showCheckBox: true,
        allowFiltering: true,
        filterType: 'Contains',
        itemsSource: 'configStore.allFieldOptions()',
        fields: {
            text: 'text', value: 'value', parentValue: 'pId', hasChildren: 'hasChild'
        },
        helpText: 'Select fields that you want to merge with your message template.',
        default: []
    },
    messageFields: {
        label: 'Message Fields',
        id: 'messageFields',
        type: 'dropdowntree',
        showCheckBox: true,
        allowFiltering: true,
        filterType: 'Contains',
        itemsSource: 'configStore.allFieldOptions()',
        fields: {
            text: 'text', value: 'value', parentValue: 'pId', hasChildren: 'hasChild'
        },
        helpText: 'Select fields that you want to merge with your message template.',
        default: []
    },
};

const messagePanelConfig = {
    cId: "messagePanel",
    cType: "messagePanel",
    cTitle: "Message Panel",
    cTemplate: `<div v-if="messagesToShow" :style="!config.showAsToast && componentStyles">
            <div v-if="config.showAsToast" :id="'toastMessageContainer_' + config.cId" class="message-panel-toast-container">
                <div class="message-panel-outer" :style="{margin: panelMargin, padding: panelPadding, 'background-color': config.backgroundColor || '', 'border-color': config.borderColor || '', 'color': config.textColor || ''}">
                    <div v-if="!showFullToastMsg">
                        <div class="toast-initial-text" v-html="config.toastInitialText"></div>
                        <button type="button" class="more-button" @click="showFullToastMsg = true">read more</button>
                        <div class="message-item-button-area" v-if="config.allowDismiss"><control v-if="config.dismissStyle == 'closeicon'" name="CLOSE_MESSAGES_ICON"/><control v-else name="CLOSE_MESSAGES"/></div>
                    </div>
                    <div v-if="showFullToastMsg">
                        <component :is="headerTemplateId" v-bind="componentMappedProps"></component>
                        <div class="message-container">
                            <div v-if="config.messageImage" :style="{ 'text-align': config.messageImageAlignment }">
                                <img class="message-image" :style="{ width: config.messageImageWidth }" :src="config.messageImage" />
                            </div>
                            <div class="message-item" v-for="(message, index) in messages">
                                <div class="message-item-text" v-html="message.text"></div>
                                <div class="message-item-button-area" v-if="config.allowDismiss && message.dismissible"><control v-if="config.dismissStyle == 'closeicon'" name="DISMISS_ICON" :actionParams="index"/><control v-else name="DISMISS" :actionParams="index"/></div>
                            </div>
                            <div class="custom-message-container message-item" v-if="config.customMessageTemplate && displayCustomMessage && !messages.length">
                                <div class="message-item-text">
                                    <component :is="messageTemplateId" v-bind="componentMappedProps"></component>
                                </div>
                                <div class="message-item-button-area" v-if="config.allowDismiss"><control v-if="config.dismissStyle == 'closeicon'" name="CLOSE_MESSAGES_ICON"/><control v-else name="CLOSE_MESSAGES"/></div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div v-else class="message-panel-inline-container">
                <backdrop :backdrop-type="config.backdropType" :backdrop-min-height="config.backdropMinHeight" :style="{'background-image': config.backgroundImage ? 'url('+config.backgroundImage+')' : ''}">
                    <div class="message-panel-outer" :style="{ margin: panelMargin }">
                        <component :is="headerTemplateId" v-bind="componentMappedProps"></component>
                        <div class="message-container">
                            <div v-if="config.messageImage" :style="{ 'text-align': config.messageImageAlignment }">
                                <img class="message-image" :style="{ width: config.messageImageWidth }" :src="config.messageImage" />
                            </div>
                            <div class="message-item" v-for="(message, index) in messages" :style="{'background-color': config.backgroundColor || '', 'border-color': config.borderColor || '', 'color': config.textColor || '', padding: itemPadding}">
                                <div class="message-item-text" v-html="message.text"></div>
                                <div class="message-item-button-area" v-if="config.allowDismiss && message.dismissible"><control v-if="config.dismissStyle == 'closeicon'" name="DISMISS_ICON" :actionParams="index"/><control v-else name="DISMISS" :actionParams="index"/></div>
                            </div>
                            <div class="custom-message-container message-item" v-if="config.customMessageTemplate && displayCustomMessage && !messages.length" :style="{'background-color': config.backgroundColor || '', 'border-color': config.borderColor || '', 'color': config.textColor || ''}">
                                <div class="message-item-text">
                                    <component :is="messageTemplateId" v-bind="componentMappedProps"></component>
                                </div>
                                <div class="message-item-button-area" v-if="config.allowDismiss"><control v-if="config.dismissStyle == 'closeicon'" name="CLOSE_MESSAGES_ICON"/><control v-else name="CLOSE_MESSAGES"/></div>
                            </div>
                        </div>
                    </div>
                </backdrop>
            </div>
    </div>`,
    controls: [
        {
            name: 'DISMISS',
            type: 'button',
            label: 'Dismiss',
            onclick: 'dismissMessage',
            text: true,
            cssClass: 'e-flat'
        }, {
            name: 'CLOSE_MESSAGES',
            type: 'button',
            label: 'Dismiss',
            onclick: 'closeCustomMessage',
            text: true,
            cssClass: 'e-flat'
        }, {
            name: 'DISMISS_ICON',
            type: 'button',
            label: 'Dismiss',
            onclick: 'dismissMessage',
            hideLabel: true,
            iconCss: 'e-icons e-close dismiss-message_panel',
            text: true,
            cssClass: 'e-flat'
        }, {
            name: 'CLOSE_MESSAGES_ICON',
            type: 'button',
            label: 'Dismiss',
            onclick: 'closeCustomMessage',
            hideLabel: true,
            iconCss: 'e-icons e-close dismiss-message_panel',
            text: true,
            cssClass: 'e-flat'
        }
    ]
};

const messagePanelCmp = {
    name: "messagePanel",
    editorSettings: editorSettings,
    data() {
        return {
            acc: false,
            vol: false,
            opp: false,
            org: false,
            slot: false,
            messageToast: false,
            messages: [],
            displayCustomMessage: false,
            showFullToastMsg: false
        };
    },
    computed: {
        headerTemplateId() {
            return 'template_' + this.cId.replace(" ", /_/g).toLowerCase() + '_header';
        },
        messageTemplateId() {
            return 'template_' + this.cId.replace(" ", /_/g).toLowerCase() + '_message';
        },
        messagesToShow() {
            return this.displayCustomMessage || this.messages.length > 0;
        },
        panelMargin() {
            return this.config.messagePanelMargin === 'custom' ? this.config.customMessagePanelMargin : this.config.messagePanelMargin;
        },
        panelPadding() {
            return this.config.messagePanelPadding === 'custom' ? this.config.customMessagePanelPadding : this.config.messagePanelPadding;
        },
        itemPadding() {
            return this.config.messageItemPadding;
        },
        componentMappedProps() {
            return {
                user: this.user,
                acc: this.acc,
                vol: this.vol,
                opp: this.opp,
                org: this.org,
                slot: this.slot
            };
        }
    },
    methods: {
        onLoad() {
            if (this.config.showAsToast && this.config.toastInitialText) {
                this.messageToast = library.showToast(false, {
                    templateElemId: '#toastMessageContainer_' + this.config.cId,
                    timeout: 600,
                    width: this.config.toastWidth || '300px',
                    cssClass: 'message-panel-toast'
                });
            }
            let mappedFields = [];
            if (this.config.mergeFields && this.config.mergeFields.length) {
                let mergeFields = this.config.mergeFields.map((field) => ({ type: 'merge', field: field }));
                mappedFields = mappedFields.concat(mergeFields);
            }
            if (this.config.messageFields && this.config.messageFields.length) {
                let messageFields = this.config.messageFields.map((field) => ({ type: 'message', field: field }));
                mappedFields = mappedFields.concat(messageFields);
            }
            // let requestFields
            if (mappedFields.length) {
                // Get fields Object from data
                let requestFields = {};
                mappedFields.forEach((fObj) => {
                    let fParts = fObj.field.split(':::');
                    let perspective = fParts[0];
                    let fieldCaId = fParts[1];
                    // Create empty array for perspectives
                    if (!requestFields[perspective]) requestFields[perspective] = [];
                    // Add fields to perspectives for requests
                    if (requestFields?.[perspective].indexOf(fieldCaId) === -1) {
                        requestFields[perspective].push(fieldCaId);
                    }
                });
                let isDismissible = (dKey, dField) => dField.indexOf('CA#') !== -1;
                let requests = Object.keys(requestFields);
                this.messages = [];
                requests.forEach((key) => {
                    this[key] = false;
                    const recordId = library.getByPath(this, `${key}Id`);
                    this.sDs.post('sds_getProfileField', key, recordId, requestFields[key]).then((response) => {
                        if (response.status === 'success') {
                            let recordId = Object.keys(response.data)[0];
                            let resFields = Object.keys(response.data[recordId]);
                            let pspObj = {};
                            resFields.forEach((rfieldName) => {
                                let matchName = key + ':::' + rfieldName;
                                let fFields = mappedFields.filter((mField) => mField.field === matchName);
                                fFields.forEach((fObj) => {
                                    let type = fObj.type;
                                    let field = fObj.field.split(':::')[1];
                                    if (type === 'merge') pspObj[field] = response.data[recordId][field];
                                    else if (type === 'message' && field !== 'ID') {
                                        this.messages.push({
                                            text: response.data[recordId][field],
                                            dismissible: isDismissible(key, field)
                                        });
                                    }
                                });
                            });
                            // For user, acc, vol, opp, org, slot in data
                            this[key] = pspObj;
                        }
                    });
                });
            }
        },
        onPropsUpdate() {
            let createComp = (template, type) => {
                let iTemplateCmpId = "template_"
                    + this.cId.replace(" ", /_/g).toLowerCase()
                    + "_" + type;
                let iTemplate = "<div>" + template + "</div>";
                library.createTemplateComponent(iTemplateCmpId, {
                    template: iTemplate,
                    useTemplateComponentMixins: true,
                    props: ['user', 'acc', 'vol', 'opp', 'org', 'slot']
                });
                return iTemplateCmpId;
            };
            if (this.templatesGenerated) return;
            if (this.config) {
                this.templatesGenerated = true;
                this.displayCustomMessage = true;
                createComp(this.config.headingTemplate, 'header');
                createComp(this.config.customMessageTemplate, 'message');
            }
        },
        dismissMessage(event, thisControl, msgIndex) {
            this.messages.splice(msgIndex, 1);
            // TODO clear out the UDF field if dismissible
        },
        dismissMessagePanel() {
            this.$destroy();
            this.$el.parentNode.removeChild(this.$el);
        },
        closeCustomMessage() {
            if (this.messageToast) {
                this.messageToast.hide();
            }
            this.displayCustomMessage = false;
        }
    },
    components: {
        backdrop,
        "ejs-button": defineAsyncComponent(() => import('@common/syncfusion/button')),
    }
};

componentStore.initComponent(messagePanelCmp, messagePanelConfig, '@common/messagePanel.vue');

export default messagePanelCmp;
</script>

<style scoped>
    .custom-message-container {
        width: fit-content;
    }
    .message-item, .message-panel-toast-container .message-panel-outer {
        background: #fff6d4;
        border: 1px solid #ffdb95;
        border-radius: 4px;
        margin-bottom: 5px;
        display: flex;
    }
    .message-panel-toast-container .message-item {
        background: transparent;
        border: 0 none;
        margin-bottom: 0;
    }
    .message-panel-inline-container .message-item {
        position: relative;
        min-height: 40px;
    }
    .message-item-text {
        flex-grow: 9;
        padding: 15px;
        font-size: 16px;
    }
    .message-item-button-area .e-control {
        display: inline-block;
    }
    .message-image {
        display: inline-block;
    }
    .message-panel-toast-container {
        width: 100%;
    }
    .message-panel-toast-container .toast-initial-text {
        margin: 10px 0 0 0;
    }
    .message-panel-toast-container .message-panel-outer {
        padding: 10px;
    }
    .message-panel-toast-container .message-item-button-area,
    .message-item-button-area {
        position: absolute;
        top: 0;
        right: 0;
    }
    .more-button {
        text-decoration: underline;
        border: 0;
        padding: 5px;
        margin: 0;
        background: transparent;
    }
</style>
